import { useEffect, useRef } from "react";
import type { FrameTarget } from "@archlang/scene/board";
import { clampRing, ringTargets, type RingContext } from "./scopeRings";

/**
 * Scope dial — **⌃⌥↑ / ⌃⌥↓** step the attention radius OUT / IN through semantic
 * rings (node → neighbourhood → space → parent). Each press reframes at the new
 * ring immediately and sets it as the session's altitude (the caller feeds that
 * back through the operator's `expandTarget`, so later edits follow at the same
 * radius).
 *
 * `e.code`-matched + capture phase (survives editor focus). Mac-gated by the
 * caller: on Windows/Linux ⌃⌥↑/↓ is Monaco's add-cursor-above/below.
 */
export interface ScopeDialOpts {
  /** Register listeners only when true (interactive operator, on, macOS). */
  readonly active: boolean;
  /** The anchor to build rings around — caret node, else board selection. */
  readonly anchorId: () => string | null;
  readonly neighbors: (id: string) => readonly string[];
  readonly focalPathOf: (id: string) => readonly string[] | null;
  readonly frameAtRing: (target: FrameTarget) => void;
  readonly ring: number;
  readonly setRing: (ring: number) => void;
}

/** True on macOS, where ⌃⌥↑/↓ is free (⌥⌘↑/↓ is Monaco's there). */
export function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const p = navigator.platform || navigator.userAgent || "";
  return /Mac|iPhone|iPad|iPod/i.test(p);
}

export function useScopeDial(opts: ScopeDialOpts): void {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (!opts.active) return;
    const step = (delta: number): void => {
      const o = optsRef.current;
      const anchor = o.anchorId();
      if (!anchor) return;
      const next = clampRing(o.ring + delta);
      if (next === o.ring) return; // already at the rail
      const ctx: RingContext = { neighbors: o.neighbors, focalPathOf: o.focalPathOf };
      const target = ringTargets(next, anchor, ctx);
      if (!target) return; // unresolvable anchor
      o.frameAtRing(target);
      o.setRing(next);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (!e.ctrlKey || !e.altKey || e.metaKey || e.shiftKey) return;
      if (e.code === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        step(+1); // out
      } else if (e.code === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        step(-1); // in
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.active]);
}
